/**
 * Build a `t.me/c/…` deep link for one message, client-side.
 *
 * SOURCE OF TRUTH: `get_telegram_link` in `backend/app/api/messages.py` — the
 * same arithmetic, deliberately duplicated. The chat-preview panel renders a
 * link control on EVERY bubble, and the endpoint takes one round trip per
 * message: a 50-message page would fire 50 requests, each running its own
 * query, just to recompute a formula the browser already has the inputs for.
 * The endpoint stays the single caller-friendly API for one-off lookups
 * (`useTelegramLink`, used by the entry/suggestion cards); keep the two in sync
 * if the backend ever learns public `@username` links.
 *
 * `peerId` is `chats.chat_id` — the Telegram-marked id, NOT the internal
 * `chats.id`. It is nullable in the DB (`bigint(20) DEFAULT NULL`), and a null
 * peer has no link at all, so this returns null rather than inventing one.
 */
export function buildTelegramLink(
  peerId: number | null | undefined,
  messageId: number | null | undefined
): string | null {
  if (peerId === null || peerId === undefined || !messageId) return null;
  // -100 prefix strip for channels/supergroups; positive ids are used as-is.
  const numericId = peerId < 0 ? Math.abs(peerId) - 1000000000000 : Math.abs(peerId);
  if (numericId <= 0) return null;
  return `https://t.me/c/${numericId}/${messageId}`;
}
